import React, { useEffect, useState } from 'react';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import { MetaTags } from 'react-meta-tags';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { Button, Nav, NavItem, TabContent, TabPane } from 'reactstrap';
import { MoonLoader } from 'react-spinners';
import 'boxicons';

import { GuidelineLayout } from './styles';
import { SendCardLayout } from './components/Send-Card/styles';
import Breadcrumbs from '../../components/Breadcrumb';
import Shimmer from '../../components/Shimmer';
import PageLoading from './components/Page-Loading';
import TableResumeReview from './components/Table-Resume-Review';
import ContainerEditContent from './components/Container-Edit-Content';
import PublishedData from './components/Published-Data';
import PerformanceData from './components/Performance-Data';
import TitleGuideline from './components/Title-Guideline';
import CmsIntegration from './components/CMS-Integration';
import ButtonBack from './components/Button-Back';

const PublishContent = () => {
  const firebaseHelper = getFirebaseBackend();
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentAccountId = user?.account;
  const { t: translate } = useTranslation();
  const [content, setContent] = useState();
  const [accountData, setAccountData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [publishedDate, setPublishedDate] = useState(new Date());
  const [publishedUrl, setPublishedUrl] = useState('');
  const [activeNav, setActiveNav] = useState('1');
  const [isSending, setIsSending] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    const loadedGuideline = await firebaseHelper.getFirebaseGuideline(currentAccountId, id);

    if (loadedGuideline.column !== 'APPROVED') navigate(`/old-guideline/${id}`);

    if (loadedGuideline.publishedDate) {
      setPublishedDate(new Date(loadedGuideline.publishedDate));
    }

    const data = await firebaseHelper.getAccount(currentAccountId);

    setIsProcessing(loadedGuideline.processing);
    setIsError(loadedGuideline?.error);
    setAccountData(data);
    setContent(loadedGuideline);
    setIsLoading(false);
  };

  const onSubmit = async () => {
    try {
      setIsSending(true);
      const isProduction = process.env.REACT_APP_BUILD_TYPE === 'production';

      if (!publishedUrl.trim()) {
        setIsSending(false);
        return;
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/publishContent`,
        {
          accountId: currentAccountId,
          guidelineId: id,
          development: !isProduction,
          publishedDate: publishedDate.getTime(),
          publishedUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${await firebaseHelper.getIdToken()}`,
          },
        },
      );

      setTimeout(() => {
        setIsSending(false);
        navigate('/old-guidelines');
      }, 2000);
    } catch (error) {
      setIsSending(false);
      console.log(error);
    }
  };

  async function handleRetry() {
    try {
      if (!id || !currentAccountId) throw new Error('Guideline ID or Account ID is missing');
      const isProduction = process.env.REACT_APP_BUILD_TYPE === 'production';
      const firebaseToken = await firebaseHelper.getIdToken();
      setIsProcessing(true);
      setIsError(false);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/approveContent`,
        {
          accountId,
          guidelineId: id,
          development: !isProduction,
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );
    } catch (error) {
      setIsProcessing(false);
      setIsError(true);
      console.log(error);
    }
  }

  useEffect(() => {
    if (!isProcessing && !isError) return;

    const db = getDatabase();
    const guidelineRef = ref(db, `guidelines/${currentAccountId}/${id}`);

    const unsubscribe = onValue(guidelineRef, (snapshot) => {
      const guidelineData = snapshot.val();

      if (!guidelineData) {
        navigate('/404');
        return;
      }

      if (guidelineData?.column !== 'APPROVED') {
        navigate(`/old-guideline/${id}`);
        return;
      }

      if (guidelineData?.processing === false || guidelineData?.error === false) {
        fetchData();
      }
    });

    return () => {
      off(guidelineRef, 'value', unsubscribe);
    };
  }, [isProcessing, isError]);

  useEffect(() => {
    fetchData();
  }, [user]);

  useEffect(() => {
    if (!isPageLoading) return;

    const db = getDatabase();
    const guidelineRef = ref(db, `guidelines/${currentAccountId}/${id}`);

    const handleValueChange = (snapshot) => {
      const data = snapshot.val();

      if (!data?.processing) {
        navigate(`/old-guideline/${id}`);
      }
    };

    const unsubscribe = onValue(guidelineRef, handleValueChange);

    return () => {
      unsubscribe();
    };
  }, [isPageLoading]);

  return (
    <GuidelineLayout>
      <div className="page-content">
        <MetaTags>
          <title>{`${translate('Publish Content')} | Ectools`}</title>
        </MetaTags>
        <Breadcrumbs title={translate('Content')} breadcrumbItem={translate('Publish Content')} />

        {isError && (
          <PageLoading
            title={translate('An error occurred')}
            description={translate('There was an error processing this guideline.')}
            button={translate('Try Again')}
            onClick={handleRetry}
            type="danger"
          >
            <box-icon name="x-circle" color="#F56565" size="7rem" />
          </PageLoading>
        )}

        {isProcessing && !isError && (
          <PageLoading
            title={translate('This guideline is being processed')}
            description={translate(
              'This may take a few minutes, you can also wait for this process in the background and return later.',
            )}
            button={translate('Go back')}
            onClick={() => navigate('/old-guidelines')}
          />
        )}

        {!isProcessing && !isError && (
          <>
            {isPageLoading ? (
              <PageLoading
                title={translate('Recreating content')}
                description={translate(
                  'This may take a few minutes, you can also wait for this process in the background and return later.',
                )}
                button={translate('Go back')}
                onClick={() => navigate('/old-guidelines')}
              />
            ) : (
              <div className="container-row">
                <div className="container-general-information">
                  <div className="header-general-information">
                    <div>
                      <ButtonBack />
                      {content?.primaryKeyword && <TitleGuideline guideline={content} />}
                    </div>
                  </div>
                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        className={activeNav === '1' ? 'actived' : ''}
                        onClick={() => setActiveNav('1')}
                      >
                        {translate('Summary')}
                      </NavLink>
                    </NavItem>
                    {!content?.googleDocId && (
                      <NavItem>
                        <NavLink
                          className={activeNav === '2' ? 'actived' : ''}
                          onClick={() => setActiveNav('2')}
                        >
                          {translate('Content')}
                        </NavLink>
                      </NavItem>
                    )}
                    <NavItem>
                      <NavLink
                        className={activeNav === '3' ? 'actived' : ''}
                        onClick={() => setActiveNav('3')}
                      >
                        {translate('Performance')}
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <TabContent activeTab={activeNav}>
                    {isLoading ? (
                      <>
                        <Shimmer height="22em" style={{ marginBottom: '3em' }} />
                        <Shimmer height="10em" style={{ marginBottom: '3em' }} />
                        <Shimmer height="15em" />
                      </>
                    ) : (
                      <TabPane tabId="1">
                        <TableResumeReview
                          data={content}
                          accountId={currentAccountId}
                          guidelineId={id}
                        />
                      </TabPane>
                    )}
                  </TabContent>
                  <TabContent activeTab={activeNav}>
                    <TabPane tabId="2">
                      <ContainerEditContent
                        content={content}
                        setIsPageLoading={setIsPageLoading}
                        noIA
                      />
                    </TabPane>
                  </TabContent>
                  <TabContent activeTab={activeNav}>
                    <TabPane tabId="3">
                      <PerformanceData
                        primaryKeyword={content?.primaryKeyword}
                        secondaryKeywords={content?.secondaryKeywords}
                      />
                    </TabPane>
                  </TabContent>
                </div>
                <div className="container-side-column">
                  <SendCardLayout>
                    <p className="subtitle">{translate('Action')}</p>
                    <Button onClick={onSubmit} disabled={!publishedUrl.trim()}>
                      {isSending ? (
                        <MoonLoader color="#fff" size={20} />
                      ) : (
                        translate('Mark as Published')
                      )}
                    </Button>
                  </SendCardLayout>
                  {isLoading ? (
                    <Shimmer height="9em" />
                  ) : (
                    accountData?.cmsAuth &&
                    content?.googleDocIdDelivery &&
                    content?.googleDocId && (
                      <CmsIntegration
                        accountData={accountData}
                        googleDocId={content?.googleDocIdDelivery}
                        title={content?.title}
                        guidelineId={id}
                        cmsLink={content?.cmsLink}
                      />
                    )
                  )}
                  {isLoading ? (
                    <Shimmer height="17em" />
                  ) : (
                    <PublishedData
                      setDate={setPublishedDate}
                      date={publishedDate}
                      setPublishedUrl={setPublishedUrl}
                      publishedUrl={publishedUrl}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </GuidelineLayout>
  );
};

export default PublishContent;
